import { useEffect, useState, useRef } from "react";

interface CoverPageProps {
  onEnter: () => void;
}

const BAR_COUNT = 20;

function MusicWaveform() {
  const [heights, setHeights] = useState<number[]>(() =>
    Array.from({ length: BAR_COUNT }, () => Math.random() * 60 + 10)
  );
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const animate = (timestamp: number) => {
      setHeights(
        Array.from({ length: BAR_COUNT }, (_, i) => {
          const phase = (i / BAR_COUNT) * Math.PI * 2;
          const slow = Math.sin(timestamp * 0.0008 + phase) * 0.5 + 0.5;
          const fast = Math.sin(timestamp * 0.003 + phase * 1.7) * 0.3 + 0.3;
          const micro = Math.sin(timestamp * 0.007 + phase * 3.1) * 0.2 + 0.2;
          const combined = slow * 0.5 + fast * 0.3 + micro * 0.2;
          return 6 + combined * 44;
        })
      );
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="flex items-end gap-[2px] md:gap-[3px] h-10 md:h-14">
      {heights.map((h, i) => (
        <div
          key={i}
          className="rounded-full flex-shrink-0"
          style={{
            width: "2px",
            height: `${h}px`,
            background: `rgba(103,232,249,${0.4 + (h / 50) * 0.6})`,
            transition: "height 0.08s ease-out",
          }}
        />
      ))}
    </div>
  );
}

export default function CoverPage({ onEnter }: CoverPageProps) {
  const [loaded, setLoaded] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 100);
    return () => clearTimeout(t);
  }, []);

  const handleEnter = () => {
    setLeaving(true);
    setTimeout(() => onEnter(), 800);
  };

  return (
    <div className="relative w-full h-screen overflow-hidden flex flex-col">
      {/* Background Image */}
      <div
        className={`absolute inset-0 transition-transform duration-[1200ms] ease-out ${leaving ? "scale-110" : "scale-100"}`}
      >
        <img
          src="https://public.readdy.ai/ai/img_res/08edcc29b6e62c5206ad5b5cb3130431.jpg"
          alt="厦门海景封面"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-900/30 via-transparent to-cyan-900/10" />
      </div>

      {/* Top Navigation */}
      <nav className="relative z-20 flex items-center justify-between px-5 md:px-8 py-4 md:py-5">
        <div className="flex items-center gap-3">
          <img
            src="https://public.readdy.ai/ai/img_res/87bc98b4-e616-459b-81b0-cc0db54579b8.png"
            alt="Logo"
            className="h-8 md:h-10 w-auto object-contain"
          />
        </div>
        <div className="hidden md:flex items-center gap-8">
          {["山海", "人文", "美食", "活力"].map((item) => (
            <span
              key={item}
              className="text-white/90 text-sm font-medium tracking-widest cursor-pointer hover:text-white transition-colors duration-200 whitespace-nowrap"
            >
              {item}
            </span>
          ))}
        </div>
        <button
          onClick={handleEnter}
          className="flex items-center gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white text-xs md:text-sm px-4 md:px-5 py-2 rounded-full hover:bg-white/25 transition-all duration-300 whitespace-nowrap cursor-pointer"
        >
          开始探索
          <i className="ri-arrow-right-line" />
        </button>
      </nav>

      {/* Main Content */}
      <div className="relative z-20 flex-1 flex flex-col justify-end pb-16 md:pb-20 px-6 md:px-16">
        <div
          className={`transition-all duration-1000 ease-out ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          {/* Tag */}
          <div className="inline-flex items-center gap-2 border border-white/50 rounded-full px-3 md:px-4 py-1 md:py-1.5 mb-4 md:mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-300 animate-pulse" />
            <span className="text-white/80 text-xs tracking-[0.15em] md:tracking-[0.2em] font-light">城市印象 · XIAMEN</span>
          </div>

          {/* Title */}
          <h1
            className="text-4xl md:text-7xl font-light text-white leading-tight mb-2 md:mb-3"
            style={{ fontFamily: "'Noto Serif SC', serif", letterSpacing: "0.05em" }}
          >
            遇见厦门
          </h1>
          <h2
            className="text-2xl md:text-5xl font-extralight text-white/90 mb-2"
            style={{ fontFamily: "'Noto Serif SC', serif", letterSpacing: "0.08em" }}
          >
            山海鹭岛
          </h2>
          <p className="text-white/70 text-sm md:text-lg font-light tracking-widest mt-3 md:mt-4 mb-5 md:mb-8">
            一城山海，万般美好
          </p>

          {/* Music Waveform */}
          <div
            className={`flex items-center gap-2 md:gap-3 mb-5 md:mb-8 transition-all duration-1000 delay-300 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
          >
            <div className="w-4 h-4 md:w-5 md:h-5 flex items-center justify-center">
              <i className="ri-music-2-line text-cyan-300 text-xs md:text-sm" />
            </div>
            <MusicWaveform />
            <span className="text-white/40 text-xs tracking-widest font-light whitespace-nowrap">晴天</span>
          </div>

          {/* CTA */}
          <div className="flex items-center gap-3 md:gap-4">
            <button
              onClick={handleEnter}
              className="group flex items-center gap-2 md:gap-3 bg-white text-gray-800 px-6 md:px-8 py-3 md:py-3.5 rounded-full text-sm font-medium hover:bg-cyan-50 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <span>开启旅程</span>
              <span className="w-5 h-5 md:w-6 md:h-6 flex items-center justify-center rounded-full bg-gray-800 group-hover:bg-cyan-600 transition-colors duration-300">
                <i className="ri-arrow-right-line text-white text-xs" />
              </span>
            </button>
            <div className="hidden sm:flex items-center gap-2 text-white/60 text-xs tracking-widest">
              <i className="ri-mouse-line text-lg" />
              <span>滑动探索</span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-5 md:bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1 animate-bounce">
        <div className="w-px h-6 md:h-8 bg-gradient-to-b from-white/0 to-white/60" />
        <i className="ri-arrow-down-s-line text-white/60 text-base md:text-lg" />
      </div>

      {/* 李可欣制作 credit */}
      <div className="absolute bottom-4 md:bottom-5 right-4 md:right-6 z-20">
        <span
          className="text-white/30 text-xs tracking-[0.12em] md:tracking-[0.15em] font-light"
          style={{ fontFamily: "'Noto Serif SC', serif" }}
        >
          李可欣 制作
        </span>
      </div>

      {/* Leaving overlay */}
      <div
        className={`absolute inset-0 z-30 bg-white transition-opacity duration-700 ${leaving ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      />
    </div>
  );
}
